import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import Modal from './Modal';
import Button from './Button';
import NumericInput from './NumericInput';
import { Select } from './Input';
import SearchableSelect from './SearchableSelect';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { loadsAPI, clientsAPI, trucksAPI, productsAPI, portsAPI, weighbridgesAPI, systemConfigAPI } from '../services/api';
import { MANAGER_LOAD_DETAILS_EDIT_CONFIG_KEY } from '../constants/systemConfigKeys';
import { formatWeight, formatDateTime } from '../utils/helpers';
import { extractTsMetaFromNotes, stripTsMetaFromNotes, upsertTsMetaInNotes, getSupplementaryTaxOption, SUPPLEMENTARY_TAX_OPTIONS, getSupplementaryTaxTotal } from '../utils/supplementaryTax';
import { useTarifConfig, mapTsOptionsForSelect } from '../hooks/useTarifConfig';
import {
  computeLoadBillAmountPreview,
  loadTariffInstant
} from '../utils/billAmountPreview';

function toIsoFromDateAndTime(dateStr, timeStr) {
  if (!dateStr) return null;
  const t = (timeStr && String(timeStr).trim()) || '00:00';
  return `${dateStr}T${t.length === 5 ? t : '00:00'}:00`;
}

/** Which weight inputs apply: tare-only (awaiting 2e pesée) vs loaded recorded without load tare, vs both. */
function getLoadWeightFieldVisibility(load) {
  if (!load) return { showTare: true, showLoaded: true };
  const hasTare =
    load.emptyWeight != null &&
    load.emptyWeight !== '' &&
    Number.isFinite(Number(load.emptyWeight));
  const hasLoaded =
    load.loadedWeight != null &&
    load.loadedWeight !== '' &&
    Number.isFinite(Number(load.loadedWeight));
  const tareOnly = hasTare && !hasLoaded;
  const loadedOnly = hasLoaded && !hasTare;
  return {
    showTare: !loadedOnly,
    showLoaded: !tareOnly,
  };
}

/** Peseur may correct tare on open tare-only loads they recorded (before marchandise weight). */
function peseurCanEditOwnTareWeight(load, userId) {
  if (!load || !userId) return false;
  const { showTare, showLoaded } = getLoadWeightFieldVisibility(load);
  if (!showTare || showLoaded) return false;
  const creatorId = load.peseurId || load.operatorId;
  if (creatorId !== userId) return false;
  const status = String(load.status || '').toUpperCase();
  if (status === 'DELIVERED' || status === 'LOADED') return false;
  return status === 'REQUESTED' || status === 'ACCEPTED';
}

const EditLoadModal = ({ isOpen, onClose, onSuccess, load }) => {
  const { t } = useTranslation();
  const { isManagerOrAdmin, isManager, isAdmin, isOperator, isPeseur, user } = useAuth();
  const [managerCanEditLoadDetails, setManagerCanEditLoadDetails] = useState(true);
  const { tarifConfig, holidayDateSet } = useTarifConfig();
  const resolvedTsOptions = tarifConfig?.tsOptions || null;
  const tsSelectOptions = mapTsOptionsForSelect(resolvedTsOptions) ||
    SUPPLEMENTARY_TAX_OPTIONS.map((opt) => ({ value: opt.value, label: `${t(opt.labelKey)} (${opt.hourlyNetAmount.toLocaleString('fr-FR')} FCFA/h)` }));
  const [loadTsMeta, setLoadTsMeta] = useState(null);
  const [tsEditMode, setTsEditMode] = useState(false);
  const [tsOptionCode, setTsOptionCode] = useState('');
  const [tsHours, setTsHours] = useState('');
  const toast = useToast();

  const [formData, setFormData] = useState({
    clientId: '',
    truckId: '',
    driverId: '',
    productId: '',
    portId: '',
    weighbridgeId: '',
    emptyWeight: '',
    loadedWeight: '',
    transactionType: 'EXPORT',
    status: 'REQUESTED',
    notes: '',
    loadedWeighDate: '',
    loadedWeighTime: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [taxesAuPoidsExpanded, setTaxesAuPoidsExpanded] = useState(false);
  
  // Dropdown data (only needed for managers/admins)
  const [clients, setClients] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [products, setProducts] = useState([]);
  const [ports, setPorts] = useState([]);
  const [weighbridges, setWeighbridges] = useState([]);
  
  const [loadingData, setLoadingData] = useState({
    clients: false,
    trucks: false,
    drivers: false,
    products: false,
    ports: false,
    weighbridges: false
  });

  useEffect(() => {
    if (!isOpen || !isManager()) {
      setManagerCanEditLoadDetails(true);
      return;
    }
    let cancelled = false;
    systemConfigAPI
      .getByKey(MANAGER_LOAD_DETAILS_EDIT_CONFIG_KEY)
      .then((res) => {
        if (cancelled) return;
        const raw = res?.data?.value;
        setManagerCanEditLoadDetails(raw === true || raw === 'true' || raw === '1' || raw === 1);
      })
      .catch(() => {
        if (!cancelled) setManagerCanEditLoadDetails(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, isManager]);

  // Initialize form data when load changes
  useEffect(() => {
    if (load && isOpen) {
      // Prefill emptyWeight from load's emptyWeight, or fallback to truck's emptyWeight
      const emptyWeightValue = load.emptyWeight 
        ? (load.emptyWeight / 1000).toString() 
        : (load.truck?.emptyWeight ? (load.truck.emptyWeight / 1000).toString() : '');
      
      const tsMeta = load.status === 'LOADED' ? extractTsMetaFromNotes(load.notes) : null;
      setLoadTsMeta(tsMeta);
      setTsEditMode(false);
      if (load.status === 'LOADED' && tsMeta) {
        setTsOptionCode(tsMeta.optionCode || '');
        setTsHours(String(tsMeta.hours ?? ''));
      } else {
        setTsOptionCode('');
        setTsHours('');
      }

      const lw =
        load.loadedWeighedAt != null
          ? {
              loadedWeighDate: new Date(load.loadedWeighedAt).toISOString().slice(0, 10),
              loadedWeighTime: `${String(new Date(load.loadedWeighedAt).getHours()).padStart(2, '0')}:${String(
                new Date(load.loadedWeighedAt).getMinutes()
              ).padStart(2, '0')}`
            }
          : load.loadDate != null
            ? {
                loadedWeighDate: new Date(load.loadDate).toISOString().slice(0, 10),
                loadedWeighTime: `${String(new Date(load.loadDate).getHours()).padStart(2, '0')}:${String(
                  new Date(load.loadDate).getMinutes()
                ).padStart(2, '0')}`
              }
            : { loadedWeighDate: '', loadedWeighTime: '' };

      setFormData({
        clientId: load.clientId || '',
        truckId: load.truckId || '',
        driverId: load.driverId || '',
        productId: load.productId || '',
        portId: load.portId || '',
        weighbridgeId: load.weighbridgeId || '',
        emptyWeight: emptyWeightValue,
        loadedWeight: load.loadedWeight !== null && load.loadedWeight !== undefined ? (load.loadedWeight / 1000).toString() : '',
        transactionType: load.transactionType || 'EXPORT',
        status: load.status || 'REQUESTED',
        notes: stripTsMetaFromNotes(load.notes),
        loadedWeighDate: lw.loadedWeighDate,
        loadedWeighTime: lw.loadedWeighTime
      });
      setErrors({});
    }
  }, [load, isOpen]);

  const loadTrucks = useCallback(async (clientId) => {
    try {
      setLoadingData(prev => ({ ...prev, trucks: true }));
      const response = await trucksAPI.getByClient(clientId);
      const trucksData = response.data.trucks || [];
      setTrucks(trucksData);
    } catch (error) {
      console.error('Error loading trucks:', error);
      setTrucks([]);
    } finally {
      setLoadingData(prev => ({ ...prev, trucks: false }));
    }
  }, []);

  const loadWeighbridges = useCallback(async (portId) => {
    try {
      setLoadingData(prev => ({ ...prev, weighbridges: true }));
      const response = await weighbridgesAPI.getByPort(portId);
      setWeighbridges(response.data.weighbridges || []);
    } catch (error) {
      console.error('Error loading weighbridges:', error);
      setWeighbridges([]);
    } finally {
      setLoadingData(prev => ({ ...prev, weighbridges: false }));
    }
  }, []);

  const loadDropdownData = useCallback(async () => {
    try {
      setLoadingData(prev => ({ ...prev, clients: true, products: true, ports: true }));
      
      const [clientsRes, productsRes, portsRes] = await Promise.all([
        clientsAPI.getAll({ limit: 500, activeOnly: true }),
        productsAPI.getAll(),
        portsAPI.getAll()
      ]);

      let nextClients = clientsRes.data.clients || [];
      const lc = load?.client;
      if (lc?.id && !nextClients.some((c) => c.id === lc.id)) {
        nextClients = [lc, ...nextClients];
      }
      setClients(nextClients);
      setProducts(productsRes.data.products || []);
      setPorts(portsRes.data.ports || []);
      
      // Load trucks for the current client
      if (load?.clientId) {
        loadTrucks(load.clientId);
      }
      
      // Load weighbridges for the current port
      if (load?.portId) {
        loadWeighbridges(load.portId);
      }
    } catch (error) {
      console.error('Error loading dropdown data:', error);
    } finally {
      setLoadingData(prev => ({ ...prev, clients: false, products: false, ports: false }));
    }
  }, [load?.clientId, load?.client, load?.portId, loadTrucks, loadWeighbridges]);

  // Load dropdown data for managers/admins
  useEffect(() => {
    if (isOpen && isManagerOrAdmin() && load) {
      loadDropdownData();
    }
  }, [isOpen, load, isManagerOrAdmin, loadDropdownData]);

  // Load trucks when client changes
  useEffect(() => {
    if (isManagerOrAdmin() && formData.clientId) {
      loadTrucks(formData.clientId);
    } else {
      setTrucks([]);
      setDrivers([]);
    }
  }, [formData.clientId, isManagerOrAdmin, loadTrucks]);

  // Load drivers when truck changes
  useEffect(() => {
    if (isManagerOrAdmin() && formData.truckId && trucks.length > 0) {
      const selectedTruck = trucks.find(truck => truck.id === formData.truckId);
      if (selectedTruck && selectedTruck.assignedDrivers) {
        const truckDrivers = selectedTruck.assignedDrivers
          .map(dt => dt.driver)
          .filter(Boolean);
        setDrivers(truckDrivers);
      } else {
        setDrivers([]);
      }
    } else {
      setDrivers([]);
    }
  }, [formData.truckId, trucks, isManagerOrAdmin]);

  // Load weighbridges when port changes
  useEffect(() => {
    if (isManagerOrAdmin() && formData.portId) {
      loadWeighbridges(formData.portId);
    } else {
      setWeighbridges([]);
    }
  }, [formData.portId, isManagerOrAdmin, loadWeighbridges]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const loadedBelowTareWarning = (() => {
    if (!formData.loadedWeight || !formData.emptyWeight) return null;
    const loadedNum = parseFloat(formData.loadedWeight);
    const emptyNum = parseFloat(formData.emptyWeight);
    if (!Number.isFinite(loadedNum) || !Number.isFinite(emptyNum) || loadedNum >= emptyNum) return null;
    return t('weighing.validations.loadedWeightGreater');
  })();

  const validateForm = () => {
    const newErrors = {};
    const { showTare, showLoaded } = getLoadWeightFieldVisibility(load);
    const canPeseurEditTare =
      isPeseur() && peseurCanEditOwnTareWeight(load, user?.id);

    if ((isManagerOrAdmin() || canPeseurEditTare) && showTare) {
      if (!formData.emptyWeight) {
        newErrors.emptyWeight = t('weighing.validations.emptyWeightRequired');
      } else if (isNaN(formData.emptyWeight) || parseFloat(formData.emptyWeight) < 0) {
        newErrors.emptyWeight = t('weighing.validations.validWeightRequired');
      }
    }

    if (showLoaded) {
      if (!formData.loadedWeight) {
        newErrors.loadedWeight = t('weighing.validations.loadedWeightRequired');
      } else if (isNaN(formData.loadedWeight) || parseFloat(formData.loadedWeight) < 0) {
        newErrors.loadedWeight = t('weighing.validations.validWeightRequired');
      }
    }

    if (isManagerOrAdmin() && showTare && showLoaded && formData.emptyWeight && formData.loadedWeight) {
      const emptyWeight = parseFloat(formData.emptyWeight);
      const loadedWeight = parseFloat(formData.loadedWeight);
      if (loadedWeight <= emptyWeight) {
        newErrors.loadedWeight = t('weighing.validations.loadedWeightGreater');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateNetWeight = () => {
    const loadedWeight = parseFloat(formData.loadedWeight) || 0;
    const noUnit = { noUnit: true };

    // For Admin/Manager, calculate net weight if empty weight is available
    if (isManagerOrAdmin() && formData.emptyWeight) {
      const emptyWeight = parseFloat(formData.emptyWeight) || 0;
      const netWeightKg = (loadedWeight - emptyWeight) * 1000;
      return netWeightKg > 0 ? formatWeight(netWeightKg, 'ton', noUnit) : formatWeight(0, 'ton', noUnit);
    }

    // For clients or when empty weight is not available, show loaded weight
    return loadedWeight > 0 ? formatWeight(loadedWeight * 1000, 'ton', noUnit) : formatWeight(0, 'ton', noUnit);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isReadOnly) return;
    
    if (!validateForm()) {
      return;
    }

    // Build TS metadata from current editor fields so Save persists latest edits
    let effectiveTsMeta = loadTsMeta;
    const rawTsHours = String(tsHours ?? '').trim();
    const hasTsInput = Boolean(tsOptionCode) || Boolean(rawTsHours);
    const parsedTsHours = Number(rawTsHours);
    if (formData.status === 'LOADED') {
      if (hasTsInput) {
        if (!tsOptionCode) {
          toast.error(t('loads.ts.validation.optionRequired'));
          return;
        }
        if (!Number.isFinite(parsedTsHours) || parsedTsHours <= 0) {
          toast.error(t('loads.ts.validation.hoursPositive'));
          return;
        }

        const option = getSupplementaryTaxOption(tsOptionCode, resolvedTsOptions);
        effectiveTsMeta = {
          optionCode: tsOptionCode,
          hours: parsedTsHours,
          totalAmount: getSupplementaryTaxTotal(tsOptionCode, parsedTsHours, resolvedTsOptions),
          description: option ? (option.description || t(option.labelKey)) : (loadTsMeta?.description || '')
        };
      } else {
        // Blank TS inputs in LOADED mode mean TS was intentionally removed.
        effectiveTsMeta = null;
      }
    } else {
      effectiveTsMeta = null;
    }
    
    setLoading(true);
    
    try {
      const hasCertificate = load.hasCertificate || false;
      const weightsFrozenByCertificate = hasCertificate;

      // Re-attach TS metadata to notes when status is LOADED; strip when not
      const cleanNotes = formData.notes ? String(formData.notes).trim() : '';
      const finalNotes = formData.status === 'LOADED' && effectiveTsMeta
        ? upsertTsMetaInNotes(cleanNotes, effectiveTsMeta)
        : stripTsMetaFromNotes(cleanNotes || '') || null;

      const updateData = {
        notes: finalNotes
      };

      // Status: peseurs cannot PUT status (server); operators/managers/admins can
      if (!isPeseur() && formData.status !== load.status) {
        updateData.status = formData.status;
      }

      // Weights / transaction type when not blocked by certificate
      if (!weightsFrozenByCertificate) {
        const { showTare, showLoaded } = getLoadWeightFieldVisibility(load);
        const canPeseurEditTare =
          isPeseur() && peseurCanEditOwnTareWeight(load, user?.id);
        if (showLoaded) {
          updateData.loadedWeight = parseFloat(formData.loadedWeight) * 1000;
          if (isPeseur() && formData.loadedWeighDate) {
            updateData.loadedWeighedAt = toIsoFromDateAndTime(
              formData.loadedWeighDate,
              formData.loadedWeighTime
            );
          }
        }
        const role = String(user?.role || '').toUpperCase();
        const staffCannotEditRequested =
          load?.status === 'REQUESTED' &&
          ['MANAGER', 'PESEUR'].includes(role);
        const maySendTransactionType =
          !staffCannotEditRequested &&
          (isPeseur() ||
            isAdmin() ||
            role === 'SUPER' ||
            (role === 'MANAGER' && managerCanEditLoadDetails) ||
            (isOperator() && role === 'CLIENT' && String(load?.status || '').toUpperCase() === 'REQUESTED'));
        if (maySendTransactionType) {
          updateData.transactionType = formData.transactionType;
        }

        if ((isManagerOrAdmin() || canPeseurEditTare) && showTare) {
          updateData.emptyWeight = parseFloat(formData.emptyWeight) * 1000;
        }

        // Header information (only for managers/admins)
        if (isManagerOrAdmin()) {
          if (formData.clientId) updateData.clientId = formData.clientId;
          if (formData.truckId) updateData.truckId = formData.truckId;
          if (formData.productId) updateData.productId = formData.productId;
          if (formData.portId) updateData.portId = formData.portId;
          if (formData.weighbridgeId) updateData.weighbridgeId = formData.weighbridgeId;
          if (formData.driverId !== undefined) updateData.driverId = formData.driverId || null;
        }
      }
      
      await loadsAPI.update(load.id, updateData);
      onSuccess(load.id);
    } catch (error) {
      console.error('Edit load error:', error);
      const errorMessage = error.response?.data?.message || t('loads.crud.updateError');
      toast.error(errorMessage);
      
      if (error.response?.data?.errors) {
        const fieldErrors = {};
        error.response.data.errors.forEach(err => {
          fieldErrors[err.path] = err.msg;
        });
        setErrors(fieldErrors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        clientId: '',
        truckId: '',
        driverId: '',
        productId: '',
        portId: '',
        weighbridgeId: '',
        emptyWeight: '',
        loadedWeight: '',
        transactionType: 'EXPORT',
        status: 'REQUESTED',
        notes: '',
        loadedWeighDate: '',
        loadedWeighTime: ''
      });
      setClients([]);
      setTrucks([]);
      setDrivers([]);
      setProducts([]);
      setPorts([]);
      setWeighbridges([]);
      setErrors({});
      setLoadTsMeta(null);
      setTsEditMode(false);
      setTsOptionCode('');
      setTsHours('');
      onClose();
    }
  };

  const handleApplyTs = () => {
    const parsedHours = Number(tsHours);
    if (!tsOptionCode || !Number.isFinite(parsedHours) || parsedHours <= 0) return;
    const totalAmount = getSupplementaryTaxTotal(tsOptionCode, parsedHours, resolvedTsOptions);
    const option = getSupplementaryTaxOption(tsOptionCode, resolvedTsOptions);
    setLoadTsMeta({
      optionCode: tsOptionCode,
      hours: parsedHours,
      totalAmount,
      description: option ? (option.description || t(option.labelKey)) : ''
    });
    setTsEditMode(false);
  };

  const handleRemoveTs = () => {
    setLoadTsMeta(null);
    setTsOptionCode('');
    setTsHours('');
    setTsEditMode(false);
  };

  const tsInlineTotal = (tsOptionCode && Number(tsHours) > 0)
    ? getSupplementaryTaxTotal(tsOptionCode, Number(tsHours), resolvedTsOptions)
    : 0;
  const resolvedTarifPoids = useMemo(() => (
    tarifConfig?.tarifPoids || {
      weightCeilingTon: 20,
      ouvrable: { minimumHt: 4499, rateHtPerTon: 153 },
      special: { minimumHt: 9000, rateHtPerTon: 302 }
    }
  ), [tarifConfig?.tarifPoids]);
  const taxesAuPoidsPreview = useMemo(() => {
    const loadedTon = parseFloat(formData.loadedWeight);
    const emptyTon = parseFloat(formData.emptyWeight);

    let netWeightKg = 0;
    if (Number.isFinite(loadedTon) && Number.isFinite(emptyTon) && loadedTon > emptyTon) {
      netWeightKg = (loadedTon - emptyTon) * 1000;
    } else if (Number.isFinite(Number(load?.netWeight)) && Number(load.netWeight) > 0) {
      netWeightKg = Number(load.netWeight);
    }

    const tariffInstant = formData.loadedWeighDate
      ? new Date(toIsoFromDateAndTime(formData.loadedWeighDate, formData.loadedWeighTime))
      : loadTariffInstant(load);

    return computeLoadBillAmountPreview({
      netWeightKg,
      tariffInstant,
      notes: formData.notes,
      loadTsMeta,
      tsInlineTotal,
      amountTsFromLoad: load?.amountTs,
      tarifPoids: resolvedTarifPoids,
      tvaRate: tarifConfig?.tvaRate,
      holidayDateSet
    });
  }, [
    formData.loadedWeight,
    formData.emptyWeight,
    formData.loadedWeighDate,
    formData.loadedWeighTime,
    load?.netWeight,
    load?.loadDate,
    load?.loadedWeighedAt,
    load?.amountTs,
    loadTsMeta,
    tsInlineTotal,
    resolvedTarifPoids,
    tarifConfig?.tvaRate,
    holidayDateSet
  ]);

  const isLoaded = formData.status === 'LOADED';

  if (!load) return null;

  const { showTare: showTareWeightField, showLoaded: showLoadedWeightField } =
    getLoadWeightFieldVisibility(load);

  /** Charged load: tare already recorded — keep tare in data/summary but omit the editable tare field. */
  const hideTareWeightInput = showTareWeightField && showLoadedWeightField;
  /** Gross / marchandise edit: net is redundant (summary or equals loaded). */
  const hideNetWeightRow =
    showLoadedWeightField && (!showTareWeightField || hideTareWeightInput);

  const emptyTonRow = parseFloat(formData.emptyWeight);
  const loadedTonRow = parseFloat(formData.loadedWeight);
  const tareWeightSummaryDisplay =
    Number.isFinite(emptyTonRow) && formData.emptyWeight !== ''
      ? formatWeight(emptyTonRow * 1000, 'ton')
      : '\u2014';
  const loadedWeightSummaryDisplay =
    Number.isFinite(loadedTonRow) && formData.loadedWeight !== ''
      ? formatWeight(loadedTonRow * 1000, 'ton')
      : '\u2014';
  const tareWeighDateTimeDisplay = load.tareWeighedAt ? formatDateTime(load.tareWeighedAt) : '\u2014';
  const loadedPassIsoForSummary =
    load.loadedWeighedAt || toIsoFromDateAndTime(formData.loadedWeighDate, formData.loadedWeighTime);
  const loadedWeighDateTimeDisplay = loadedPassIsoForSummary
    ? formatDateTime(loadedPassIsoForSummary)
    : '\u2014';

  /** Status <option>s must include the load's current value or the browser shows the first option (e.g. Demande while load is Accepté). */
  const editLoadStatusOptions = (() => {
    const order = ['REQUESTED', 'ACCEPTED', 'LOADED', 'DELIVERED'];
    const allowed = new Set(['REQUESTED']);
    if (isManagerOrAdmin()) {
      allowed.add('ACCEPTED');
      allowed.add('LOADED');
    }
    if (isManagerOrAdmin() || (isOperator() && load?.status === 'LOADED')) {
      allowed.add('DELIVERED');
    }
    if (load?.status) allowed.add(load.status);
    return order.filter((code) => allowed.has(code));
  })();

  const hasCertificate = load?.hasCertificate === true;
  /** Any certificate coverage locks load editing entirely. */
  const weightsFrozenByCertificate = hasCertificate;
  const cannotEditRequestedAsStaff =
    load?.status === 'REQUESTED' &&
    ['MANAGER', 'PESEUR'].includes(String(user?.role || '').toUpperCase());
  const isReadOnly = weightsFrozenByCertificate || cannotEditRequestedAsStaff;
  const transactionTypeAndStatusReadOnly =
    isReadOnly || (isManager() && !managerCanEditLoadDetails);
  const peseurLoadedInlineRow =
    isPeseur() && !weightsFrozenByCertificate && showLoadedWeightField;
  const canPeseurEditTare =
    isPeseur() && peseurCanEditOwnTareWeight(load, user?.id);
  const canEditTareWeight = isManagerOrAdmin() || canPeseurEditTare;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('loads.crud.editLoadTitle')}
      size="lg"
      closeOnOverlayClick={false}
    >
      {weightsFrozenByCertificate && (
        <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded p-3">
          <p className="text-sm text-yellow-800">
            {t('loads.crud.certificateGeneratedWarning')}
          </p>
        </div>
      )}
      {cannotEditRequestedAsStaff && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded p-3">
          <p className="text-sm text-amber-900">
            {t('loads.crud.requestedNotEditableByStaff')}
          </p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Load Info - Editable for Managers/Admins, Read-only for others or if certificate exists */}
        {isManagerOrAdmin() ? (
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-900">
              {t('loads.crud.loadInformation')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Client Selection */}
              <div>
                <SearchableSelect
                  label={`${t('weighing.selectClient')}`}
                  name="clientId"
                  value={formData.clientId}
                  onChange={handleInputChange}
                  options={clients.map(client => ({
                    value: client.id,
                    label: client.companyName
                  }))}
                  placeholder={t('weighing.placeholders.selectClient')}
                  disabled={loadingData.clients || isReadOnly}
                  required
                  error={errors.clientId}
                />
                {errors.clientId && <p className="text-red-500 text-sm mt-1">{errors.clientId}</p>}
              </div>

              {/* Truck Selection — full row so inline label + field have room */}
              <div className="md:col-span-2 w-full min-w-0">
                <SearchableSelect
                  label={`${t('weighing.selectTruck')}`}
                  name="truckId"
                  value={formData.truckId}
                  inlineLabel
                  onChange={handleInputChange}
                  options={trucks.map(truck => {
                    const driverNames = truck.assignedDrivers?.map(dt => 
                      `${dt.driver?.firstName || ''} ${dt.driver?.lastName || ''}`.trim()
                    ).filter(Boolean) || [];
                    let label = truck.plateNumber;
                    if (driverNames.length > 0) {
                      label += ` - ${driverNames.join(', ')}`;
                    }
                    return { value: truck.id, label };
                  })}
                  placeholder={formData.clientId ? t('weighing.placeholders.selectTruck') : t('weighing.placeholders.selectClientFirst')}
                  disabled={!formData.clientId || loadingData.trucks || isReadOnly}
                  required
                  error={errors.truckId}
                />
              </div>

              {/* Driver Selection - Show whenever the selected truck has assigned drivers */}
              {formData.truckId && drivers.length > 0 && (
                <div>
                  <SearchableSelect
                    label={`${t('weighing.selectDriver')}`}
                    name="driverId"
                    value={formData.driverId}
                    onChange={handleInputChange}
                    options={drivers.map(driver => ({
                      value: driver.id,
                      label: `${driver.firstName} ${driver.lastName}${driver.username ? ` (@${driver.username})` : ''}${driver.phone ? ` - ${driver.phone}` : ''}`
                    }))}
                    placeholder={t('weighing.placeholders.selectDriver')}
                    disabled={loadingData.drivers || isReadOnly}
                    required
                    error={errors.driverId}
                  />
                  {errors.driverId && <p className="text-red-500 text-sm mt-1">{errors.driverId}</p>}
                </div>
              )}

              {/* Product Selection */}
              <div>
                <SearchableSelect
                  label={`${t('weighing.selectProduct')}`}
                  name="productId"
                  value={formData.productId}
                  onChange={handleInputChange}
                  options={products.map(product => ({
                    value: product.id,
                    label: product.name
                  }))}
                  placeholder={t('weighing.placeholders.selectProduct')}
                  disabled={loadingData.products || isReadOnly}
                  required
                  error={errors.productId}
                />
                {errors.productId && <p className="text-red-500 text-sm mt-1">{errors.productId}</p>}
              </div>

              {/* Port Selection */}
              <div>
                <Select
                  label={`${t('weighing.selectPort')} *`}
                  name="portId"
                  value={formData.portId}
                  onChange={handleInputChange}
                  options={ports.map(port => ({
                    value: port.id,
                    label: `${port.name} - ${port.location}`
                  }))}
                  placeholder={t('weighing.placeholders.selectPort')}
                  disabled={loadingData.ports || isReadOnly}
                  className={errors.portId ? 'border-red-300' : ''}
                />
                {errors.portId && <p className="text-red-500 text-sm mt-1">{errors.portId}</p>}
              </div>

              {/* Weighbridge Selection */}
              <div>
                <Select
                  label={`${t('weighing.selectWeighbridge')} *`}
                  name="weighbridgeId"
                  value={formData.weighbridgeId}
                  onChange={handleInputChange}
                  options={weighbridges.map(weighbridge => ({
                    value: weighbridge.id,
                    label: `${weighbridge.name} - ${weighbridge.location}`
                  }))}
                  placeholder={formData.portId ? t('weighing.placeholders.selectWeighbridge') : t('weighing.placeholders.selectPortFirst')}
                  disabled={!formData.portId || loadingData.weighbridges || isReadOnly}
                  className={errors.weighbridgeId ? 'border-red-300' : ''}
                />
                {errors.weighbridgeId && <p className="text-red-500 text-sm mt-1">{errors.weighbridgeId}</p>}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-3">
              {t('loads.crud.loadInformation')}
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">{t('tables.client')}:</span>
                <span className="ml-2 font-medium">{load.client?.companyName}</span>
              </div>
              <div>
                <span className="text-gray-600">{t('tables.truck')}:</span>
                <span className="ml-2 font-medium">{load.truck?.plateNumber}</span>
              </div>
              <div>
                <span className="text-gray-600">{t('tables.product')}:</span>
                <span className="ml-2 font-medium">{load.product?.name}</span>
              </div>
              <div>
                <span className="text-gray-600">{t('navigation.ports')}:</span>
                <span className="ml-2 font-medium">{load.port?.name}</span>
              </div>
            </div>
          </div>
        )}

        {/* Transaction type + status on one row (stack on narrow screens) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="transactionType" className="block text-sm font-medium text-gray-700 mb-1">
              {t('weighing.transactionType')} *
            </label>
            <select
              id="transactionType"
              name="transactionType"
              value={formData.transactionType}
              onChange={handleInputChange}
              disabled={transactionTypeAndStatusReadOnly}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                transactionTypeAndStatusReadOnly ? 'bg-gray-100 text-gray-700 cursor-not-allowed' : ''
              }`}
            >
              <option value="EXPORT">{t('weighing.export')}</option>
              <option value="IMPORT">{t('weighing.import')}</option>
            </select>
          </div>
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              {t('tables.status')} *
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              disabled={transactionTypeAndStatusReadOnly}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                transactionTypeAndStatusReadOnly ? 'bg-gray-100 text-gray-700 cursor-not-allowed' : ''
              }`}
            >
              {editLoadStatusOptions.map((code) => (
                <option key={code} value={code}>
                  {t(`loads.statuses.${String(code).toLowerCase()}`)}
                </option>
              ))}
            </select>
          </div>
          {isOperator() && (
            <div className="md:col-span-2">
              <p className="text-xs text-gray-500 mt-0 md:-mt-1">
                {load?.status === 'LOADED'
                  ? t('loads.operatorDeliveredInfo')
                  : t('loads.operatorStatusInfo')}
              </p>
            </div>
          )}
        </div>

        {/* Peseur: marchandise weight + date/time — above « Informations de poids » */}
        {peseurLoadedInlineRow && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
              <div className="min-w-[11rem] flex-1">
                <label htmlFor="loadedWeight" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('weighing.loadedWeight')} *
                </label>
                <NumericInput
                  id="loadedWeight"
                  name="loadedWeight"
                  value={formData.loadedWeight}
                  onChange={handleInputChange}
                  placeholder={t('weighing.placeholders.loadedWeight')}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.loadedWeight ? 'border-red-300' : 'border-gray-300'
                  } ${isReadOnly ? 'bg-gray-100 text-gray-700 cursor-not-allowed' : ''}`}
                />
              </div>
              <div className="min-w-[10.5rem] w-full sm:w-auto">
                <label htmlFor="loadedWeighDate" className="block text-xs font-medium text-gray-600 mb-1">
                  {t('weighing.twoStep.loadedPassDate')}
                </label>
                <input
                  id="loadedWeighDate"
                  type="date"
                  name="loadedWeighDate"
                  value={formData.loadedWeighDate}
                  onChange={handleInputChange}
                  className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="min-w-[8.5rem] w-full sm:w-auto">
                <label htmlFor="loadedWeighTime" className="block text-xs font-medium text-gray-600 mb-1">
                  {t('loads.modal.dateTime')}
                </label>
                <input
                  id="loadedWeighTime"
                  type="time"
                  name="loadedWeighTime"
                  value={formData.loadedWeighTime}
                  onChange={handleInputChange}
                  className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>
            {errors.loadedWeight && <p className="text-red-500 text-sm">{errors.loadedWeight}</p>}
            {loadedBelowTareWarning && !errors.loadedWeight && (
              <p className="text-amber-700 text-sm bg-amber-50 px-2 py-1 rounded" role="alert">
                {loadedBelowTareWarning}
              </p>
            )}
          </div>
        )}

        {/* Weight Information */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            {t('weighing.weightInformation')}
          </h4>
          {(showTareWeightField || showLoadedWeightField) && (
            <div className="text-sm rounded-md bg-slate-50 px-3 py-2 border border-slate-100 space-y-2 mb-4">
              {showTareWeightField && (
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <div className="min-w-0">
                    <span className="font-medium text-gray-700">{t('weighing.emptyWeight')}</span>
                    <span className="ml-2 font-semibold text-gray-900">{tareWeightSummaryDisplay}</span>
                  </div>
                  <span className="text-gray-600 shrink-0 tabular-nums text-right">{tareWeighDateTimeDisplay}</span>
                </div>
              )}
              {showLoadedWeightField && (
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <div className="min-w-0">
                    <span className="font-medium text-gray-700">{t('weighing.loadedWeight')}</span>
                    <span className="ml-2 font-semibold text-gray-900">{loadedWeightSummaryDisplay}</span>
                  </div>
                  <span className="text-gray-600 shrink-0 tabular-nums text-right">{loadedWeighDateTimeDisplay}</span>
                </div>
              )}
            </div>
          )}
          <div className="space-y-4">
            {/* Empty Weight — hidden for gross-only loads, and hidden on charged loads (tare shown in summary only) */}
            {showTareWeightField && !hideTareWeightInput && (
              <div>
                <label htmlFor="emptyWeight" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('weighing.emptyWeight')} *
                </label>
                <NumericInput
                  id="emptyWeight"
                  name="emptyWeight"
                  value={formData.emptyWeight}
                  onChange={handleInputChange}
                  placeholder={t('weighing.placeholders.emptyWeight')}
                  readOnly={!canEditTareWeight || isReadOnly}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none ${
                    (!canEditTareWeight || isReadOnly)
                      ? 'bg-gray-100 text-gray-700 cursor-not-allowed'
                      : 'focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                  } ${
                    errors.emptyWeight ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {isPeseur() && !canPeseurEditTare && (
                  <p className="text-xs text-gray-500 mt-1">
                    {t('loads.peseurTareEditCreatorOnly')}
                  </p>
                )}
                {!isPeseur() && !isManagerOrAdmin() && (
                  <p className="text-xs text-gray-500 mt-1">
                    {t('loads.emptyWeightRestricted')}
                  </p>
                )}
                {errors.emptyWeight && <p className="text-red-500 text-sm mt-1">{errors.emptyWeight}</p>}
              </div>
            )}

            {/* Loaded Weight — non-peseur or peseur with certificate (inline row rendered above) */}
            {showLoadedWeightField && !peseurLoadedInlineRow && (
              <div>
                <label htmlFor="loadedWeightBelow" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('weighing.loadedWeight')} *
                </label>
                <NumericInput
                  id="loadedWeightBelow"
                  name="loadedWeight"
                  value={formData.loadedWeight}
                  onChange={handleInputChange}
                  placeholder={t('weighing.placeholders.loadedWeight')}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.loadedWeight ? 'border-red-300' : 'border-gray-300'
                  } ${isReadOnly ? 'bg-gray-100 text-gray-700 cursor-not-allowed' : ''}`}
                />
                {errors.loadedWeight && <p className="text-red-500 text-sm mt-1">{errors.loadedWeight}</p>}
                {loadedBelowTareWarning && !errors.loadedWeight && (
                  <p className="text-amber-700 text-sm mt-1 bg-amber-50 px-2 py-1 rounded" role="alert">
                    {loadedBelowTareWarning}
                  </p>
                )}
              </div>
            )}

            {/* Net weight — hidden for marchandise-only / charged edits (redundant with summary or gross) */}
            {showLoadedWeightField && !hideNetWeightRow && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('weighing.netWeight')}
                </label>
                <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700 font-medium">
                  {calculateNetWeight()}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Taxes Au Poids: expandable summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setTaxesAuPoidsExpanded((prev) => !prev)}
            className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-blue-100/70 transition-colors"
            aria-expanded={taxesAuPoidsExpanded}
          >
            <span className="text-sm font-medium text-blue-900">
              {taxesAuPoidsPreview.amountTs > 0
                ? t('loads.taxesAuPoids.amountTtcWithTs', 'Total TTC (Taxes Au Poids + TVA + TS)')
                : t('loads.taxesAuPoids.amountTtc', 'Total TTC (Taxes Au Poids + TVA)')}
              {' '}
              <span className="font-bold">{taxesAuPoidsPreview.amountTtc.toLocaleString('fr-FR')} FCFA</span>
            </span>
            {taxesAuPoidsExpanded ? (
              <ChevronUpIcon className="h-5 w-5 text-blue-600 shrink-0" aria-hidden />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-blue-600 shrink-0" aria-hidden />
            )}
          </button>
          {taxesAuPoidsExpanded && (
            <div className="border-t border-blue-200 px-4 py-3">
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-blue-700 font-medium">{t('loads.taxesAuPoids.netWeight', 'Poids net')}</dt>
                  <dd className="text-blue-900 mt-1 font-semibold">{taxesAuPoidsPreview.netTon.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} t</dd>
                </div>
                <div>
                  <dt className="text-blue-700 font-medium">{t('loads.taxesAuPoids.appliedTariff', 'Tarif appliqué')}</dt>
                  <dd className="text-blue-900 mt-1 font-semibold">
                    {taxesAuPoidsPreview.isOuvrable
                      ? t('loads.taxesAuPoids.ouvrable', 'Ouvrable')
                      : t('loads.taxesAuPoids.special', 'Spécial (sam/dim/nuit/férié)')}
                  </dd>
                </div>
                <div>
                  <dt className="text-blue-700 font-medium">{t('loads.taxesAuPoids.rule', 'Règle de calcul')}</dt>
                  <dd className="text-blue-900 mt-1">
                    {taxesAuPoidsPreview.netTon <= 0
                      ? t('loads.taxesAuPoids.noWeight', 'Saisir des poids valides pour calculer.')
                      : taxesAuPoidsPreview.netTon <= taxesAuPoidsPreview.ceilingTon
                        ? `${t('loads.taxesAuPoids.minRule', '≤ plafond: montant minimum')} (${taxesAuPoidsPreview.minHt.toLocaleString('fr-FR')} FCFA)`
                        : `${t('loads.taxesAuPoids.rateRule', '> plafond: tarif par tonne')} (${taxesAuPoidsPreview.rateHtPerTon.toLocaleString('fr-FR')} FCFA/t)`}
                  </dd>
                </div>
                <div>
                  <dt className="text-blue-700 font-medium">{t('loads.taxesAuPoids.ceiling', 'Plafond')}</dt>
                  <dd className="text-blue-900 mt-1">{taxesAuPoidsPreview.ceilingTon.toLocaleString('fr-FR')} t</dd>
                </div>
                <div>
                  <dt className="text-blue-700 font-medium">{t('loads.taxesAuPoids.amountHt', 'Montant HT')}</dt>
                  <dd className="text-blue-900 mt-1 font-semibold">{taxesAuPoidsPreview.amountHt.toLocaleString('fr-FR')} FCFA</dd>
                </div>
                <div>
                  <dt className="text-blue-700 font-medium">{t('loads.taxesAuPoids.amountTva', 'TVA')}</dt>
                  <dd className="text-blue-900 mt-1">{taxesAuPoidsPreview.amountTva.toLocaleString('fr-FR')} FCFA</dd>
                </div>
                {(taxesAuPoidsPreview.amountTs > 0) && (
                  <div>
                    <dt className="text-blue-700 font-medium">{t('loads.ts.detail.title', 'Taxes Supplémentaires (TS)')}</dt>
                    <dd className="text-blue-900 mt-1">{taxesAuPoidsPreview.amountTs.toLocaleString('fr-FR')} FCFA</dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>

        {/* TS section: always visible; "none" when empty, add/edit when LOADED */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-2">{t('loads.ts.detail.title')}</h4>
          {!isLoaded && (
            <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
              {t('loads.ts.tsSetWhenMarkedLoaded')}
            </p>
          )}
          {isLoaded && loadTsMeta && !tsEditMode && (
            <div className="space-y-2">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <dl className="grid grid-cols-3 gap-4">
                  <div>
                    <dt className="text-xs text-amber-700 font-medium">{t('loads.ts.detail.option')}</dt>
                    <dd className="text-sm text-amber-900 mt-1">
                      {(() => { const o = getSupplementaryTaxOption(loadTsMeta.optionCode, resolvedTsOptions); return o?.description || (o?.labelKey && t(o.labelKey)) || loadTsMeta.description; })()}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-amber-700 font-medium">{t('loads.ts.detail.hours')}</dt>
                    <dd className="text-sm text-amber-900 mt-1">{loadTsMeta.hours} h</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-amber-700 font-medium">{t('loads.ts.detail.totalAmount')}</dt>
                    <dd className="text-sm font-semibold text-amber-900 mt-1">
                      {Number(loadTsMeta.totalAmount).toLocaleString()} FCFA
                    </dd>
                  </div>
                </dl>
              </div>
              {!isReadOnly && (
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setTsEditMode(true)}>
                    {t('loads.ts.changeTs')}
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={handleRemoveTs}>
                    {t('loads.ts.removeTs')}
                  </Button>
                </div>
              )}
            </div>
          )}
          {isLoaded && (tsEditMode || !loadTsMeta) && !isReadOnly && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                <Select
                  className="min-w-0 flex-1 w-full"
                  label={t('loads.ts.optionLabel')}
                  value={tsOptionCode}
                  onChange={(e) => setTsOptionCode(e.target.value)}
                  options={tsSelectOptions}
                  placeholder={t('loads.ts.optionPlaceholder')}
                />
                <div className="w-full sm:w-44 shrink-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('loads.ts.hoursLabel')}</label>
                  <NumericInput
                    variant="integer"
                    value={tsHours}
                    onChange={(e) => setTsHours(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              {tsOptionCode && Number(tsHours) > 0 && (
                <p className="text-sm text-gray-600">
                  {t('loads.ts.preview', {
                    option: (() => { const o = getSupplementaryTaxOption(tsOptionCode, resolvedTsOptions); return o?.description || (o?.labelKey && t(o.labelKey)) || tsOptionCode; })(),
                    hours: Number(tsHours),
                    total: tsInlineTotal.toLocaleString('fr-FR')
                  })}
                </p>
              )}
              <div className="flex gap-2">
                <Button type="button" variant="primary" size="sm" onClick={handleApplyTs} disabled={!tsOptionCode || !Number(tsHours) || Number(tsHours) <= 0}>
                  {t('loads.ts.apply')}
                </Button>
                {loadTsMeta && (
                  <Button type="button" variant="outline" size="sm" onClick={() => { setTsEditMode(false); setTsOptionCode(loadTsMeta.optionCode || ''); setTsHours(String(loadTsMeta.hours ?? '')); }}>
                    {t('common.cancel')}
                  </Button>
                )}
              </div>
            </div>
          )}
          {isLoaded && !loadTsMeta && !tsEditMode && isReadOnly && (
            <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
              {t('loads.ts.noSupplementaryTax')}
            </p>
          )}
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
            {t('weighing.notes')}
          </label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            placeholder={t('weighing.notesPlaceholder')}
            disabled={isReadOnly}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={3}
          />
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            loading={loading}
            disabled={loading || isReadOnly}
          >
            {t('loads.crud.updateLoad')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default EditLoadModal;
